import React from 'react';
import { Network, TxStep, TxStatus, TxChain } from '@types';
import Button from '@components/Button';
import Icon from '@components/Icon';
import useGetExplorerUrl from '@hooks/useGetExplorerUrl';
import { shortenAddress } from '@lib/utils';
import clsx from 'clsx';

interface Props {
  depositTxnHash: string;
  transferTxnHash: string;
  depositStatus: TxStatus;
  transferStatus: TxStatus;
  network: Network;
  index: number;
  chain: TxChain | null;
  onCloseNotification: (index: number) => void;
}

function MinifiedBridgeTransactionListItem({
  depositTxnHash,
  transferTxnHash,
  depositStatus,
  transferStatus,
  network,
  index,
  onCloseNotification,
  chain,
}: Props) {
  const getExplorerUrl = useGetExplorerUrl();
  const explorerUrl = chain ? getExplorerUrl(network, chain) : '';

  const hasBridgeTransactionCompleted =
    depositStatus === TxStatus.fulfilled &&
    transferStatus === TxStatus.fulfilled;

  const hasBridgeTransactionFailed =
    depositStatus === TxStatus.failed || transferStatus === TxStatus.failed;

  const generateTransactionText = (
    step: TxStep,
    status: TxStatus,
    txnHash: string
  ) => {
    switch (step + '|' + status + '|' + txnHash) {
      case TxStep.deposit + '|' + TxStatus.pending + '|' + txnHash:
        return 'Deposit Pending';
      case TxStep.deposit + '|' + TxStatus.fulfilled + '|' + txnHash:
        return 'Deposit Received';
      case TxStep.transfer + '|' + TxStatus.pending + '|' + '':
        return 'Waiting for Deposit';
      case TxStep.transfer + '|' + TxStatus.pending + '|' + txnHash:
        return 'Transfer Initiated';
      case TxStep.transfer + '|' + TxStatus.fulfilled + '|' + txnHash:
        return 'Transfer Completed';
      default:
        return `${
          step.charAt(0).toUpperCase() + step.substr(1).toLowerCase()
        } failed`;
    }
  };

  const showProcessOrCheckIcon = (status: string, isLoading: boolean) => {
    switch (status) {
      case TxStatus.fulfilled:
        return <Icon.Check title={TxStatus.fulfilled} />;
      case TxStatus.pending:
        return (
          <Icon.Process
            className={clsx('', {
              'animate-spin-ease-in-out': isLoading,
            })}
            title={TxStatus.pending}
          />
        );
      case TxStatus.failed:
        return <Icon.Close title={TxStatus.failed} />;
      default:
        return null;
    }
  };

  const displayFailedOrNotFailedContent = () => {
    return (
      <>
        <div className="flex flex-wrap p-0.5 items-center">
          {showProcessOrCheckIcon(
            depositStatus,
            depositStatus === TxStatus.pending
          )}
          <p className="px-2">
            {generateTransactionText(
              TxStep.deposit,
              depositStatus,
              depositTxnHash
            )}
          </p>
          {depositStatus === TxStatus.failed && (
            <p
              title="error"
              className="text-red text-microLight text-left mt-1"
            >
              Transaction failed and reverted. Make sure you are paying enough
              gas fees and try again!
            </p>
          )}
        </div>

        <div className="flex flex-wrap p-0.5 mb-1 items-center">
          {showProcessOrCheckIcon(
            transferStatus,
            depositStatus === TxStatus.fulfilled &&
              transferStatus === TxStatus.pending &&
              !!transferTxnHash
          )}
          <p className="px-2">
            {generateTransactionText(
              TxStep.transfer,
              transferStatus,
              transferTxnHash
            )}
          </p>
          {transferStatus === TxStatus.failed && !!transferTxnHash && (
            <p
              title="error"
              className="text-red text-microLight text-left mt-1"
            >
              Transaction failed and reverted on the blockchain. Please check
              your transaction settings and retry.
            </p>
          )}
        </div>
        {hasBridgeTransactionCompleted && (
          <Button
            size="micro"
            hasShadow={false}
            className="text-smallRegular font-normal border-white border w-full"
            onClick={() => {
              onCloseNotification(index);
            }}
          >
            Transaction Completed!
          </Button>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-wrap flex-col">
      {(hasBridgeTransactionCompleted || hasBridgeTransactionFailed) && (
        <Button
          className="absolute right-[1px] top-[1px] bg-opacity-0"
          hasShadow={false}
          onClick={() => {
            onCloseNotification(index);
          }}
        >
          <Icon.Close title="close" />
        </Button>
      )}
      <p className="p-0.5">
        Bridging Txn:{' '}
        <a
          href={explorerUrl + depositTxnHash}
          className="text-blue"
          target="_blank"
          rel="noreferrer"
        >
          {shortenAddress(depositTxnHash)}
        </a>
      </p>
      {displayFailedOrNotFailedContent()}
    </div>
  );
}

export default MinifiedBridgeTransactionListItem;
